import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown, Building2, Users, Trophy, Ship, Sparkles, UserPlus, Palette } from 'lucide-react';
import { LogoIcon } from './Logo';
import { useEmpezar } from './EmpezarModal';
import { Button } from './ui/button';
import LanguageSwitcher from './LanguageSwitcher';
import { getLangFromPath, localizePath } from '../i18n';

const serviceSlugs = [
  { slug: 'marcas', key: 'brands', icon: Sparkles },
  { slug: 'eventos', key: 'corporate', icon: Building2 },
  { slug: 'fan-experience', key: 'fanExperience', icon: Users },
  { slug: 'deportes', key: 'sports', icon: Trophy },
  { slug: 'hospitality', key: 'hospitality', icon: Ship },
  { slug: 'fotografos-on-demand', key: 'photographersOnDemand', icon: UserPlus },
  { slug: 'productoras', key: 'productoras', icon: Palette },
];

/**
 * useLocalizedLink returns a Link component and a `to` helper that
 * automatically prefixes the current language.
 */
function useLocalizedTo() {
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  return (path) => localizePath(path, lang);
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const location = useLocation();
  const { open: openEmpezar } = useEmpezar();
  const { t } = useTranslation();
  const to = useLocalizedTo();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? 'text-white' : 'text-white/70 hover:text-white'}`;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? 'bg-[#0a0a0f]/85 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="container-mi flex items-center justify-between h-16 lg:h-20">
        <NavLink to={to('/')} className="flex items-center gap-2 group" data-testid="navbar-logo">
          <LogoIcon size={32} className="drop-shadow-lg shadow-violet-500/30 transition-transform group-hover:scale-105" />
          <span className="font-display text-xl font-semibold tracking-tight">
            moment<span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">IA</span>
          </span>
        </NavLink>

        <nav className="hidden lg:flex items-center gap-8">
          <NavLink to={to('/vende-tus-fotos')} className={linkCls}>
            {t('nav.sellPhotos')}
          </NavLink>

          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button className="flex items-center gap-1 text-sm font-medium text-white/70 hover:text-white transition-colors">
              {t('nav.services')} <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {servicesOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 z-50">
                <div className="w-[520px] bg-[#111116] border border-white/10 rounded-2xl shadow-2xl shadow-black/60 p-3 grid grid-cols-1 gap-1">
                  {serviceSlugs.map((s) => {
                    const Icon = s.icon;
                    return (
                      <NavLink
                        key={s.slug}
                        to={to(`/servicios/${s.slug}`)}
                        className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-violet-500/10 border border-violet-400/20 flex items-center justify-center text-violet-300 shrink-0 group-hover:bg-violet-500/20 transition-colors">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-white">{t(`services.${s.key}.title`)}</div>
                          <div className="text-xs text-white/60 mt-0.5">{t(`services.${s.key}.desc`)}</div>
                        </div>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <NavLink to={to('/precios')} className={linkCls}>
            {t('nav.pricing')}
          </NavLink>

          <a
            href="https://moment-ia.shop/face-search"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-white/70 hover:text-white transition-colors"
            data-testid="navbar-face-search"
          >
            <Sparkles className="w-3.5 h-3.5 text-fuchsia-400" />
            {t('nav.findMyPhotos')}
          </a>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <div
            className="relative"
            onMouseEnter={() => setRequestOpen(true)}
            onMouseLeave={() => setRequestOpen(false)}
          >
            <Button
              variant="ghost"
              data-testid="navbar-request-btn"
              className="text-white/80 hover:text-white hover:bg-white/5 gap-1.5"
            >
              {t('nav.requestDemo')}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${requestOpen ? 'rotate-180' : ''}`} />
            </Button>
            {requestOpen && (
              <div className="absolute top-full right-0 pt-2 w-72 z-50">
                <div className="rounded-2xl bg-[#111116] border border-white/10 shadow-2xl overflow-hidden">
                  <NavLink
                    to={to('/request-demo')}
                    data-testid="navbar-request-demo"
                    className="block p-4 hover:bg-white/5 transition-colors border-b border-white/5"
                    onClick={() => setRequestOpen(false)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-violet-500/15 border border-violet-400/25 flex items-center justify-center text-violet-300 shrink-0">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-white">{t('requestMenu.demoTitle')}</div>
                        <div className="text-[11px] text-white/50 mt-0.5 leading-snug">{t('requestMenu.demoDesc')}</div>
                      </div>
                    </div>
                  </NavLink>
                  <NavLink
                    to={to('/solicitar-fotografos')}
                    data-testid="navbar-request-photographers"
                    className="block p-4 hover:bg-white/5 transition-colors"
                    onClick={() => setRequestOpen(false)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-indigo-500/15 border border-indigo-400/25 flex items-center justify-center text-indigo-300 shrink-0">
                        <UserPlus className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-white">{t('requestMenu.photographersTitle')}</div>
                        <div className="text-[11px] text-white/50 mt-0.5 leading-snug">{t('requestMenu.photographersDesc')}</div>
                      </div>
                    </div>
                  </NavLink>
                </div>
              </div>
            )}
          </div>
          <Button
            onClick={openEmpezar}
            data-testid="navbar-empezar"
            className="bg-white text-[#0a0a0f] hover:bg-white/90 font-medium rounded-full px-5"
          >
            {t('nav.start')}
          </Button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher compact />
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors"
            aria-label={t('nav.menu')}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#0a0a0f] border-t border-white/5">
          <div className="container-mi py-6 flex flex-col gap-1">
            <NavLink to={to('/vende-tus-fotos')} className="py-3 text-white/80 hover:text-white text-base">
              {t('nav.sellPhotos')}
            </NavLink>
            <div className="py-2">
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2">{t('nav.services')}</div>
              <div className="flex flex-col">
                {serviceSlugs.map((s) => (
                  <NavLink
                    key={s.slug}
                    to={to(`/servicios/${s.slug}`)}
                    className="py-2 pl-3 border-l border-white/10 text-sm text-white/70 hover:text-white hover:border-violet-400"
                  >
                    {t(`services.${s.key}.title`)}
                  </NavLink>
                ))}
              </div>
            </div>
            <NavLink to={to('/precios')} className="py-3 text-white/80 hover:text-white text-base">
              {t('nav.pricing')}
            </NavLink>
            <a
              href="https://moment-ia.shop/face-search"
              className="py-3 text-white/80 hover:text-white text-base inline-flex items-center gap-1.5"
            >
              <Sparkles className="w-4 h-4 text-fuchsia-400" />
              {t('nav.findMyPhotos')}
            </a>
            <div className="flex flex-col gap-3 mt-4">
              <NavLink to={to('/request-demo')}>
                <Button variant="outline" className="w-full border-white/15 bg-transparent hover:bg-white/5">
                  {t('nav.requestDemo')}
                </Button>
              </NavLink>
              <NavLink to={to('/solicitar-fotografos')}>
                <Button variant="outline" className="w-full border-indigo-400/30 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-200">
                  {t('nav.requestPhotographers')}
                </Button>
              </NavLink>
              <Button
                onClick={() => { setMobileOpen(false); openEmpezar(); }}
                className="w-full bg-white text-[#0a0a0f] hover:bg-white/90 rounded-full"
              >
                {t('nav.start')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
